/**
 * Agent Picks - the "Agent Picks" attribute's backing id-set (see
 * @slowmaui/shared-types' attributes.ts). Populated by opening a
 * `?story=<token>` link from an agent's Buyer/Seller Message (agent_stories),
 * not by direct per-item user action like Favorites/Alerts - there's no
 * toggle button here, just "did my agent include this."
 *
 * Persists in localStorage, same anonymous-user pattern as favorites.ts/
 * alerts.ts - deliberately localStorage-only (no server-sync-when-logged-in
 * layer like favorites.ts has): the story itself is already durable server
 * side (agent_stories/share_token), what's stored here is purely this
 * browser's own view history, which is inherently a client-device concept.
 * If buyers ever complain about losing it on a new device, add a sync-when-
 * logged-in layer mirroring favorites.ts - not a new mechanism.
 *
 * Only ONE story is ever "active" (drives the map/attribute) at a time -
 * the most recently opened/reactivated one, and only for ACTIVE_WINDOW_MS
 * from that moment. Past stories aren't discarded though: they stay in
 * history (capped at MAX_HISTORY entries) so the hamburger menu's "Past
 * Agent Picks" list can show and reactivate them - reactivating resets the
 * clock, same as opening the link again.
 */

import type { DataClient } from '@slowmaui/api-client';
import { listingItemKey } from '@slowmaui/shared-types';

const STORAGE_KEY = 'slowmaui_agent_stories';
const ACTIVE_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;
const MAX_HISTORY = 10;

interface StoredStoryEntry {
  token: string;
  title: string | null;
  agentName: string | null;
  itemCount: number;
  loadedAt: string;
  itemIds: string[];
}

export interface ActiveStoryMeta {
  token: string;
  title: string | null;
  agentName: string | null;
  itemCount: number;
  loadedAt: string;
}

export interface AgentPickHistoryEntry {
  token: string;
  title: string | null;
  agentName: string | null;
  itemCount: number;
  loadedAt: string;
  isActive: boolean;
}

type ChangeCallback = (ids: Set<string>, meta: ActiveStoryMeta | null) => void;

// Newest-activated first. history[0], if within its own ACTIVE_WINDOW_MS,
// is "the" active story - there's no separate active pointer to drift out
// of sync with it.
let history: StoredStoryEntry[] = [];
let listeners: ChangeCallback[] = [];

function isActiveEntry(entry: StoredStoryEntry): boolean {
  return Date.now() - new Date(entry.loadedAt).getTime() < ACTIVE_WINDOW_MS;
}

function activeEntry(): StoredStoryEntry | null {
  const top = history[0];
  return top && isActiveEntry(top) ? top : null;
}

function toMeta(entry: StoredStoryEntry): ActiveStoryMeta {
  return {
    token: entry.token,
    title: entry.title,
    agentName: entry.agentName,
    itemCount: entry.itemCount,
    loadedAt: entry.loadedAt,
  };
}

function save(): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ history }));
  } catch (error) {
    console.error('Failed to save agent stories to localStorage:', error);
  }
}

function notify(): void {
  const active = activeEntry();
  listeners.forEach((cb) => cb(active ? new Set(active.itemIds) : new Set(), active ? toMeta(active) : null));
}

/** Moves (or inserts) an entry to the front of history, capped at MAX_HISTORY. */
function promote(entry: StoredStoryEntry): void {
  history = [entry, ...history.filter((e) => e.token !== entry.token)].slice(0, MAX_HISTORY);
}

/** Restores history from localStorage. Call once at startup, before checkUrlForStory(). */
export function initAgentPicks(): void {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed?.history)) {
        history = parsed.history;
      }
    }
  } catch (error) {
    console.error('Failed to load agent stories from localStorage:', error);
    history = [];
  }
  notify();
}

/** The active story's item-key set - empty if none, or the most recent one has expired. */
export function getAgentPicks(): Set<string> {
  const active = activeEntry();
  return active ? new Set(active.itemIds) : new Set();
}

export function getActiveStoryMeta(): ActiveStoryMeta | null {
  const active = activeEntry();
  return active ? toMeta(active) : null;
}

/** Whether there's anything at all to show in a "Past Agent Picks" menu entry. */
export function hasAgentPickHistory(): boolean {
  return history.length > 0;
}

/** Full history, newest first, each flagged with whether it's the current active one. */
export function getAgentPickHistory(): AgentPickHistoryEntry[] {
  return history.map((entry, i) => ({
    token: entry.token,
    title: entry.title,
    agentName: entry.agentName,
    itemCount: entry.itemCount,
    loadedAt: entry.loadedAt,
    isActive: i === 0 && isActiveEntry(entry),
  }));
}

export function onAgentPicksChange(callback: ChangeCallback): () => void {
  listeners.push(callback);
  return () => {
    listeners = listeners.filter((l) => l !== callback);
  };
}

/**
 * Reactivates an already-seen story from history (the "Past Agent Picks"
 * menu) - no network call, uses the item set cached at the time it was
 * first opened. Resets its clock to now, same as opening the link again.
 * Returns the reactivated meta, or null if the token isn't in history.
 */
export function activateStory(token: string): ActiveStoryMeta | null {
  const existing = history.find((e) => e.token === token);
  if (!existing) return null;

  const reactivated: StoredStoryEntry = { ...existing, loadedAt: new Date().toISOString() };
  promote(reactivated);
  save();
  notify();
  return toMeta(reactivated);
}

/**
 * Checks the current URL for `?story=<token>`. If present and not already
 * the active entry, fetches it fresh and promotes it to active. Returns
 * the newly-loaded meta (so the caller can show a toast) - null if there
 * was no `?story=` param, the fetch failed, or it matched the already-
 * active story (no-op, not re-toasted on a plain refresh).
 */
export async function checkUrlForStory(api: DataClient): Promise<ActiveStoryMeta | null> {
  const token = new URLSearchParams(window.location.search).get('story');
  if (!token) return null;
  const current = activeEntry();
  if (current?.token === token) return null;

  try {
    const story = await api.getStory(token);
    const ids = new Set<string>();
    story.listings.forEach((l) => ids.add(listingItemKey(l)));
    story.neighborhoods.forEach((n) => ids.add(`nh:${n.id}`));

    const entry: StoredStoryEntry = {
      token,
      title: story.title ?? null,
      agentName: story.agentName ?? null,
      itemCount: ids.size,
      loadedAt: new Date().toISOString(),
      itemIds: [...ids],
    };
    promote(entry);
    save();
    notify();
    return toMeta(entry);
  } catch (error) {
    console.error('Failed to load agent story:', error);
    return null;
  }
}
